import { useEffect } from "react";

interface SeoProps {
  title: string;
  description?: string;
  /** Social share image - absolute URL, or a site-root path like "/brand/og.jpg". */
  image?: string;
  /** Set true on auth-gated pages so search engines do not index them. */
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = "/brand/og.jpg";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Resolve a path or absolute URL to an absolute URL using the current origin. */
function absolute(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (typeof window === "undefined") return pathOrUrl;
  return `${window.location.origin}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/**
 * Minimal SEO/head manager. For the public marketing pages this will be paired
 * with build-time pre-rendering (docs/ARCHITECTURE.md SEO note) so crawlers get
 * real HTML; this keeps the head correct at runtime in the meantime.
 */
export function Seo({ title, description, image, noindex }: SeoProps) {
  useEffect(() => {
    document.title = title;

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:card", "summary_large_image");

    const img = absolute(image ?? DEFAULT_OG_IMAGE);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:image", img);

    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    if (typeof window !== "undefined") {
      upsertMeta("property", "og:url", window.location.href);
      upsertLink("canonical", `${window.location.origin}${window.location.pathname}`);
    }
  }, [title, description, image, noindex]);

  return null;
}
